/**
 * WAR Breakdown Display — Professional visualization of WAR v2 components
 * Shows batting, baserunning, fielding, positional, and park adjustments
 */
import React from 'react';
import { Box, Typography, Chip, useTheme, alpha, Tooltip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { motion } from 'framer-motion';

const WAR_TIERS = [
  { min: 5.0, label: 'MVP Caliber', emoji: '🏆', color: '#ff6b35' },
  { min: 3.0, label: 'All-Star', emoji: '⭐', color: '#ffa726' },
  { min: 2.0, label: 'Starter', emoji: '✅', color: '#66bb6a' },
  { min: 1.0, label: 'Role Player', emoji: '👍', color: '#42a5f5' },
  { min: 0.0, label: 'Bench', emoji: '📋', color: '#78909c' },
  { min: -Infinity, label: 'Below Replacement', emoji: '📉', color: '#ef5350' }
];

const getWARTier = (war) => WAR_TIERS.find(t => war >= t.min) || WAR_TIERS[WAR_TIERS.length - 1];

const WARDisplay = ({ warData, gamesPlayed = 162, compact = false }) => {
  const theme = useTheme();
  
  if (!warData && warData !== 0) return null;
  
  const war = typeof warData === 'number' ? warData : (warData?.total || warData?.war || 0);
  const components = typeof warData === 'object' ? warData : null;

  // Project to 162-game pace for tier grading when sample is small
  const gp = Math.max(gamesPlayed || 1, 1);
  const projectedWar = gp < 40 ? Math.min(war * (162 / gp), 15) : war;
  const isPaced = gp < 40 && war !== 0;
  const tier = getWARTier(projectedWar);
  
  // Build bar chart data if we have component breakdown
  const chartData = components ? [
    { name: 'Batting', value: components.batting || 0 },
    { name: 'Baserunning', value: components.baserunning || 0 },
    { name: 'Fielding', value: components.fielding || 0 },
    { name: 'Position', value: components.positional || 0 },
    { name: 'Park Adj', value: components.parkAdj || 0 },
    { name: 'Replacement', value: components.replacement || 0 },
  ].filter(d => d.value !== 0) : null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Box>
        {/* WAR Score Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: compact ? 1 : 1.5 }}>
          <Box sx={{
            px: compact ? 2 : 2.5,
            py: compact ? 0.5 : 0.75,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(tier.color, 0.12)} 0%, ${alpha(tier.color, 0.04)} 100%)`,
            border: `1.5px solid ${alpha(tier.color, 0.25)}`,
            textAlign: 'center',
          }}>
            <Typography variant={compact ? 'h5' : 'h4'} fontWeight={800} sx={{ color: tier.color, lineHeight: 1.1, fontSize: compact ? '1.3rem' : '1.8rem' }}>
              {war.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.5 }}>
              WAR{isPaced ? ` (${gp} GP)` : ''}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'left' }}>
            <Chip
              label={`${tier.emoji} ${tier.label}`}
              size="small"
              sx={{
                bgcolor: alpha(tier.color, 0.1),
                color: tier.color,
                fontWeight: 700,
                fontSize: '0.75rem',
                border: `1px solid ${alpha(tier.color, 0.2)}`,
                height: 24,
              }}
            />
            {isPaced && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.secondary', opacity: 0.7 }}>
                Pace: {projectedWar.toFixed(1)} / 162G
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Component Bar Chart */}
        {!compact && chartData && chartData.length > 0 && (
          <Box sx={{ width: '100%', height: 180, mt: 1 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ left: 5, right: 15, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.15)} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: theme.palette.text.secondary, fontWeight: 600 }} width={76} axisLine={false} tickLine={false} />
                <ReferenceLine x={0} stroke={alpha(theme.palette.divider, 0.4)} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.value >= 0 ? alpha(theme.palette.success.main, 0.7) : alpha(theme.palette.error.main, 0.7)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Inline component summary when no chart */}
        {!compact && (!chartData || chartData.length === 0) && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Component breakdown unavailable</Typography>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default WARDisplay;
export { WAR_TIERS, getWARTier };
